/**
 * Analytics Data Service
 *
 * Service for fetching, processing, and aggregating analytics data from GraphQL
 */

import { generateClient, GraphQLResult } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { getAnalyticsAggregate, listAnalyticsAggregates } from "../../graphql/queries";
import {
	AnalyticsAggregate,
	MetricType,
	AnalyticsFilters,
	AnalyticsQueryResult,
	UserMetrics,
	GroupMetrics,
	SystemMetrics,
	DashboardData,
	DateRange,
	TimeSeriesData,
} from "../../types/analytics";
// ModelAnalyticsAggregateFilterInput - using any type for filter input to avoid dependency on generated API file
type ModelAnalyticsAggregateFilterInput = any;

/**
 * Fetch analytics aggregates from GraphQL API
 */
export async function fetchAnalyticsAggregates(filters: AnalyticsFilters = {}): Promise<AnalyticsQueryResult> {
	try {
		const client = generateClient({ authMode: "userPool" });
		const filter = buildGraphQLFilter(filters);
		const response = (await client.graphql({
			query: listAnalyticsAggregates,
			variables: { filter, nextToken: filters.nextToken },
		})) as any;

		const items = response.data?.listAnalyticsAggregates?.items || [];
		const nextToken = response.data?.listAnalyticsAggregates?.nextToken;

		return {
			aggregates: items.map(transformRawAggregate),
			nextToken,
			hasMore: !!nextToken,
		};
	} catch (error) {
		console.error("Failed to fetch analytics aggregates:", error);
		throw new Error(`Failed to fetch analytics data: ${error instanceof Error ? error.message : "Unknown error"}`);
	}
}

/**
 * Fetch a single analytics aggregate by ID
 */
export async function fetchAnalyticsAggregate(id: string): Promise<AnalyticsAggregate | null> {
	try {
		const client = generateClient({ authMode: "userPool" });
		const response = (await client.graphql({
			query: getAnalyticsAggregate,
			variables: { id },
		})) as any;

		const item = response.data?.getAnalyticsAggregate;
		return item ? transformRawAggregate(item) : null;
	} catch (error) {
		console.error("Failed to fetch analytics aggregate:", error);
		return null;
	}
}

/**
 * Build GraphQL filter from analytics filters
 */
function buildGraphQLFilter(filters: AnalyticsFilters): ModelAnalyticsAggregateFilterInput {
	const filter: ModelAnalyticsAggregateFilterInput = {};

	if (filters.metricType) {
		filter.metricType = { eq: filters.metricType };
	}

	if (filters.groupId) {
		filter.groupId = { eq: filters.groupId };
	}

	if (filters.dateRange) {
		// Convert date range to period filter
		// For hourly: YYYY-MM-DD-HH format
		// For daily: YYYY-MM-DD format
		const startPeriod = formatDateToPeriod(filters.dateRange.startDate, "hourly");
		const endPeriod = formatDateToPeriod(filters.dateRange.endDate, "hourly");

		filter.period = {
			between: [startPeriod, endPeriod],
		};
	}

	return filter;
}

/**
 * Transform raw GraphQL response to typed aggregate
 */
function transformRawAggregate(item: any): AnalyticsAggregate {
	return {
		id: item.id,
		metricType: item.metricType,
		period: item.period,
		value: item.value,
		groupId: item.groupId,
		metadata: typeof item.metadata === "string" ? JSON.parse(item.metadata) : item.metadata,
		ttl: item.ttl,
		createdAt: item.createdAt,
		updatedAt: item.updatedAt,
	};
}

/**
 * Format date to period string
 */
function formatDateToPeriod(dateStr: string, type: "hourly" | "daily"): string {
	const date = new Date(dateStr);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");

	if (type === "daily") {
		return `${year}-${month}-${day}`;
	} else {
		const hour = String(date.getHours()).padStart(2, "0");
		return `${year}-${month}-${day}-${hour}`;
	}
}

/**
 * Process aggregates into dashboard data
 */
export function processAggregatesIntoDashboard(aggregates: AnalyticsAggregate[], dateRange: DateRange): DashboardData {
	const userMetrics: UserMetrics[] = [];
	const groupMetrics: GroupMetrics[] = [];
	const systemMetrics: SystemMetrics[] = [];

	// Separate aggregates by type
	for (const aggregate of aggregates) {
		if (aggregate.metricType.includes("user")) {
			userMetrics.push(extractUserMetrics(aggregate));
		} else if (aggregate.metricType.includes("group")) {
			groupMetrics.push(extractGroupMetrics(aggregate));
		} else if (aggregate.metricType.includes("system")) {
			systemMetrics.push(extractSystemMetrics(aggregate));
		}
	}

	// Calculate summary statistics
	const summary = calculateSummary(userMetrics, groupMetrics, systemMetrics);

	// Generate time series data
	const timeSeriesData = generateTimeSeriesData(aggregates);

	return {
		dateRange,
		summary,
		userMetrics,
		groupMetrics,
		systemMetrics,
		timeSeriesData,
	};
}

/**
 * Extract user metrics from aggregate metadata
 */
function extractUserMetrics(aggregate: AnalyticsAggregate): UserMetrics {
	const metadata = aggregate.metadata;

	return {
		userId: metadata.userId || "unknown",
		period: aggregate.period,
		totalEvents: aggregate.value,
		uniqueSessions: metadata.uniqueSessions || 0,
		formStarts: metadata.formStarts || 0,
		formCreates: metadata.formCreates || 0,
		formGroupCreates: metadata.formGroupCreates || 0,
		formGroupEdits: metadata.formGroupEdits || 0,
		fieldEdits: metadata.fieldEdits || 0,
		refreshes: metadata.refreshes || 0,
		pageViews: metadata.pageViews || 0,
		routeChanges: metadata.routeChanges || 0,
		historyReverts: metadata.historyReverts || 0,
		exports: metadata.exports || 0,
		projectsCreated: metadata.projectsCreated || 0,
		templatesCreated: metadata.templatesCreated || 0,
		templatesEdited: metadata.templatesEdited || 0,
		averageSessionDuration: metadata.averageSessionDuration,
		totalSessionDuration: metadata.totalSessionDuration,
		mostActiveHour: metadata.mostActiveHour,
		eventTypes: metadata.eventTypes || {},
		deviceBreakdown: metadata.deviceBreakdown || {},
		appVersions: metadata.appVersions || {},
		topUrls: metadata.topUrls || [],
		formTemplateIds: metadata.formTemplateIds || [],
		window: metadata.window || { type: "hourly", period: aggregate.period, startTime: "", endTime: "" },
	};
}

/**
 * Extract group metrics from aggregate metadata
 */
function extractGroupMetrics(aggregate: AnalyticsAggregate): GroupMetrics {
	const metadata = aggregate.metadata;

	return {
		groupId: aggregate.groupId || "unknown",
		period: aggregate.period,
		totalEvents: aggregate.value,
		uniqueSessions: metadata.uniqueSessions || 0,
		uniqueUsers: metadata.uniqueUsers || 0,
		totalProjectsCreated: metadata.totalProjectsCreated || 0,
		totalTemplatesCreated: metadata.totalTemplatesCreated || 0,
		totalExports: metadata.totalExports || 0,
		averageUserActivity: metadata.averageUserActivity || 0,
		topEventTypes: metadata.topEventTypes || [],
		eventTypes: metadata.eventTypes || {},
		deviceDistribution: metadata.deviceDistribution || {},
		window: metadata.window || { type: "hourly", period: aggregate.period, startTime: "", endTime: "" },
	};
}

/**
 * Extract system metrics from aggregate metadata
 */
function extractSystemMetrics(aggregate: AnalyticsAggregate): SystemMetrics {
	const metadata = aggregate.metadata;

	return {
		period: aggregate.period,
		totalEvents: aggregate.value,
		uniqueSessions: metadata.uniqueSessions || 0,
		uniqueUsers: metadata.uniqueUsers || 0,
		uniqueGroups: metadata.uniqueGroups || 0,
		totalFormStarts: metadata.totalFormStarts || 0,
		totalExports: metadata.totalExports || 0,
		systemLoad: metadata.systemLoad || 0,
		errorRate: metadata.errorRate || 0,
		averageResponseTime: metadata.averageResponseTime,
		peakHour: metadata.peakHour,
		eventTypes: metadata.eventTypes || {},
		formTemplateIds: metadata.formTemplateIds || [],
		window: metadata.window || { type: "hourly", period: aggregate.period, startTime: "", endTime: "" },
	};
}

/**
 * Calculate summary statistics across all metrics
 */
function calculateSummary(userMetrics: UserMetrics[], groupMetrics: GroupMetrics[], systemMetrics: SystemMetrics[]): DashboardData["summary"] {
	const totalUsers = new Set(userMetrics.map((m) => m.userId)).size;
	const totalSessions = userMetrics.reduce((sum, m) => sum + m.uniqueSessions, 0);
	const totalEvents = userMetrics.reduce((sum, m) => sum + m.totalEvents, 0);
	const totalExports = userMetrics.reduce((sum, m) => sum + m.exports, 0);

	const sessionDurations = userMetrics.filter((m) => m.averageSessionDuration !== undefined).map((m) => m.averageSessionDuration!);

	const averageSessionDuration = sessionDurations.length > 0 ? sessionDurations.reduce((sum, duration) => sum + duration, 0) / sessionDurations.length : 0;

	return {
		totalUsers,
		totalSessions,
		totalEvents,
		totalExports,
		averageSessionDuration: Math.round(averageSessionDuration),
	};
}

/**
 * Generate time series data for charts
 */
function generateTimeSeriesData(aggregates: AnalyticsAggregate[]): TimeSeriesData {
	const hourlyData = new Map<string, { events: number; users: Set<string>; sessions: number }>();
	const dailyData = new Map<string, { events: number; users: Set<string>; sessions: number; exports: number }>();
	const eventTypeCounts = new Map<string, number>();
	const deviceTypeCounts = new Map<string, number>();

	for (const aggregate of aggregates) {
		const metadata = aggregate.metadata;

		// Process hourly data
		if (aggregate.metricType.includes("hourly")) {
			const existing = hourlyData.get(aggregate.period) || { events: 0, users: new Set(), sessions: 0 };
			existing.events += aggregate.value;
			existing.sessions += metadata.uniqueSessions || 0;
			if (metadata.userId) {
				existing.users.add(metadata.userId);
			}
			hourlyData.set(aggregate.period, existing);
		}

		// Process daily data
		const dayPeriod = aggregate.period.substring(0, 10); // Extract YYYY-MM-DD
		const dailyExisting = dailyData.get(dayPeriod) || {
			events: 0,
			users: new Set(),
			sessions: 0,
			exports: 0,
		};
		dailyExisting.events += aggregate.value;
		dailyExisting.sessions += metadata.uniqueSessions || 0;
		dailyExisting.exports += metadata.exports || 0;
		if (metadata.userId) {
			dailyExisting.users.add(metadata.userId);
		}
		dailyData.set(dayPeriod, dailyExisting);

		// Process event types
		if (metadata.eventTypes) {
			for (const [eventType, count] of Object.entries(metadata.eventTypes)) {
				eventTypeCounts.set(eventType, (eventTypeCounts.get(eventType) || 0) + (count as number));
			}
		}

		// Process device types
		if (metadata.deviceBreakdown) {
			for (const [deviceType, count] of Object.entries(metadata.deviceBreakdown)) {
				deviceTypeCounts.set(deviceType, (deviceTypeCounts.get(deviceType) || 0) + (count as number));
			}
		}
	}

	// Convert to arrays for charts
	const hourlyActivity = Array.from(hourlyData.entries())
		.map(([hour, data]) => ({
			hour,
			events: data.events,
			users: data.users.size,
			sessions: data.sessions,
		}))
		.sort((a, b) => a.hour.localeCompare(b.hour));

	const dailyActivity = Array.from(dailyData.entries())
		.map(([date, data]) => ({
			date,
			events: data.events,
			users: data.users.size,
			sessions: data.sessions,
			exports: data.exports,
		}))
		.sort((a, b) => a.date.localeCompare(b.date));

	const totalEventTypeCount = Array.from(eventTypeCounts.values()).reduce((sum, count) => sum + count, 0);
	const eventTypeBreakdown = Array.from(eventTypeCounts.entries())
		.map(([eventType, count]) => ({
			eventType,
			count,
			percentage: totalEventTypeCount > 0 ? Math.round((count / totalEventTypeCount) * 100) : 0,
		}))
		.sort((a, b) => b.count - a.count);

	const totalDeviceTypeCount = Array.from(deviceTypeCounts.values()).reduce((sum, count) => sum + count, 0);
	const deviceTypeBreakdown = Array.from(deviceTypeCounts.entries())
		.map(([deviceType, count]) => ({
			deviceType,
			count,
			percentage: totalDeviceTypeCount > 0 ? Math.round((count / totalDeviceTypeCount) * 100) : 0,
		}))
		.sort((a, b) => b.count - a.count);

	return {
		hourlyActivity,
		dailyActivity,
		eventTypeBreakdown,
		deviceTypeBreakdown,
	};
}

/**
 * Helper function to create default date range (last 7 days)
 */
export function createDefaultDateRange(): DateRange {
	const endDate = new Date();
	const startDate = new Date(endDate);
	startDate.setDate(startDate.getDate() - 7);

	return {
		startDate: startDate.toISOString(),
		endDate: endDate.toISOString(),
	};
}

/**
 * Helper function to validate date range
 */
export function validateDateRange(dateRange: DateRange): boolean {
	const start = new Date(dateRange.startDate);
	const end = new Date(dateRange.endDate);

	return start <= end && start <= new Date() && end <= new Date();
}
